import { addMonths, isSameDay, monthGrid, startOfDay } from '@crm/shared';
import { Button, cn } from '@crm/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Autocomplete } from '@/components';
import type { Appointment } from '../api';
import { CreateMenu } from './create-menu';

// Rotulos com chave propria: 'S' e 'Q' se repetem na semana em pt-BR.
const WEEK_DAYS = [
  { key: 'dom', label: 'D' },
  { key: 'seg', label: 'S' },
  { key: 'ter', label: 'T' },
  { key: 'qua', label: 'Q' },
  { key: 'qui', label: 'Q' },
  { key: 'sex', label: 'S' },
  { key: 'sab', label: 'S' },
];

const monthLabel = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

export interface AgendaFilters {
  professionalIds: string[];
  customerIds: string[];
}

interface Person {
  id: string;
  name: string;
}

interface AgendaSidebarProps {
  reference: Date;
  onSelectDay: (day: Date) => void;
  appointments: Appointment[];
  filters: AgendaFilters;
  onChangeFilters: (filters: AgendaFilters) => void;
  canWrite: boolean;
  onCreate: () => void;
  onCreateLink: () => void;
}

/** Mini calendario do topo da barra lateral, igual ao do Google Agenda. */
function MiniCalendar({
  reference,
  onSelectDay,
}: {
  reference: Date;
  onSelectDay: (day: Date) => void;
}) {
  const [month, setMonth] = useState(() => startOfDay(reference));
  const today = new Date();

  // Mudar o dia na agenda (ou clicar em Hoje) traz o mini calendario junto.
  useEffect(() => {
    setMonth(startOfDay(reference));
  }, [reference]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium first-letter:uppercase">{monthLabel.format(month)}</p>
        <div className="flex">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Mes anterior"
            onClick={() => setMonth((current) => addMonths(current, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Proximo mes"
            onClick={() => setMonth((current) => addMonths(current, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEK_DAYS.map((day) => (
          <span key={day.key} className="text-xs text-muted-foreground">
            {day.label}
          </span>
        ))}

        {monthGrid(month).map((day) => {
          const isCurrentMonth = day.getMonth() === month.getMonth();

          return (
            <button
              type="button"
              key={day.toISOString()}
              onClick={() => onSelectDay(day)}
              className={cn(
                // No toque o dia precisa de alvo maior; no desktop volta ao mini.
                'mx-auto flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors hover:bg-muted lg:h-7 lg:w-7 lg:text-xs',
                !isCurrentMonth && 'text-muted-foreground/60',
                isSameDay(day, today) && 'font-medium text-primary',
                isSameDay(day, reference) && 'bg-primary text-primary-foreground hover:bg-primary',
              )}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Coluna esquerda da agenda: criar, mini calendario e as buscas de pessoas.
 *
 * Sem ninguem escolhido nao ha filtro e a agenda mostra todos os
 * profissionais e clientes.
 *
 * Os candidatos saem dos agendamentos do periodo carregado - assim o filtro
 * funciona para qualquer perfil, sem depender de permissao para listar
 * usuarios da empresa.
 */
export function AgendaSidebar({
  reference,
  onSelectDay,
  appointments,
  filters,
  onChangeFilters,
  canWrite,
  onCreate,
  onCreateLink,
}: AgendaSidebarProps) {
  /** Guarda o nome de quem foi escolhido, caso saia do periodo em vista. */
  const [chosenNames, setChosenNames] = useState<Record<string, string>>({});

  const { professionals, customers } = useMemo(() => {
    const unique = (list: Person[]) =>
      [...new Map(list.map((person) => [person.id, person])).values()].sort((a, b) =>
        a.name.localeCompare(b.name),
      );

    return {
      professionals: unique(
        appointments
          .filter((item) => item.professionalId && item.professionalName)
          .map((item) => ({
            id: item.professionalId as string,
            name: item.professionalName as string,
          })),
      ),
      customers: unique(
        appointments.map((item) => ({ id: item.customerId, name: item.customerName })),
      ),
    };
  }, [appointments]);

  /** Guarda os rotulos escolhidos, para nao virarem id solto fora do periodo. */
  function remember(chosen: { id: string; label: string }[]) {
    setChosenNames((current) => ({
      ...current,
      ...Object.fromEntries(chosen.map((item) => [item.id, item.label])),
    }));
  }

  const nameOf = (id: string, people: Person[]) =>
    people.find((person) => person.id === id)?.name ?? chosenNames[id] ?? id;

  const hasFilters = filters.professionalIds.length + filters.customerIds.length > 0;

  return (
    <div className="space-y-4">
      {/* No celular o Criar mora na barra do topo, sempre a vista; aqui ele
          apareceria so com o painel aberto. */}
      {canWrite ? (
        <CreateMenu
          className="hidden lg:block"
          onNewAppointment={onCreate}
          onNewLink={onCreateLink}
        />
      ) : null}

      {/* No celular a troca de dia ja esta nas setas do topo: o mini
          calendario so roubava altura antes da grade. */}
      <div className="hidden lg:block">
        <MiniCalendar reference={reference} onSelectDay={onSelectDay} />
      </div>

      <div className="space-y-3 lg:border-t lg:border-border lg:pt-5">
        <Autocomplete
          placeholder="Filtrar por profissionais"
          variant="filter"
          size="lg"
          multiple
          options={professionals.map((person) => ({ id: person.id, label: person.name }))}
          value={filters.professionalIds.map((id) => ({ id, label: nameOf(id, professionals) }))}
          onChange={(chosen) => {
            remember(chosen);
            onChangeFilters({ ...filters, professionalIds: chosen.map((item) => item.id) });
          }}
        />

        <Autocomplete
          placeholder="Filtrar por clientes"
          variant="filter"
          size="lg"
          multiple
          options={customers.map((person) => ({ id: person.id, label: person.name }))}
          value={filters.customerIds.map((id) => ({ id, label: nameOf(id, customers) }))}
          onChange={(chosen) => {
            remember(chosen);
            onChangeFilters({ ...filters, customerIds: chosen.map((item) => item.id) });
          }}
        />
      </div>

      {hasFilters ? (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:underline"
          onClick={() => onChangeFilters({ professionalIds: [], customerIds: [] })}
        >
          Limpar filtro
        </button>
      ) : (
        <p className="text-xs text-muted-foreground">
          Sem filtro: a agenda mostra todos os profissionais e clientes.
        </p>
      )}
    </div>
  );
}
